import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { GameState, Position, PieceType, IMove, IPiece } from "..\/..\/..\/..\/types";
import { MCTSUtils_v2 } from "..\/..\/..\/utils\/MCTSUtils";

/**
 * Priority 24: King Backup Support
 *
 * When the king is stuck with few safe moves (indicating that King Safety
 * priorities filtered out most dangerous moves), prioritize moving other
 * pieces closer to the king for protection and support.
 *
 * This prevents pieces from advancing aimlessly when the king needs help,
 * creating better coordination and tactical opportunities.
 */
export class KingBackupSupport extends BaseDecisionPriority {
  readonly priority = 24;
  readonly name = "King Backup Support";
  readonly description =
    "Move pieces closer to protect stuck king - higher priority than strategic advance";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const gameState = context.gameState;
    const allMoves = context.allPossibleMoves;

    // Check if our king is stuck/trapped
    const kingStuckInfo = this.detectKingStuckSituation(gameState, allMoves);

    // Only relevant if king is actually stuck
    return kingStuckInfo.isStuck && kingStuckInfo.kingPosition !== null;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];
    const gameState = context.gameState;
    const allMoves = context.allPossibleMoves;

    // Check if our king is stuck/trapped
    const kingStuckInfo = this.detectKingStuckSituation(gameState, allMoves);

    if (!kingStuckInfo.isStuck || !kingStuckInfo.kingPosition) {
      // This shouldn't happen if isRelevant is working correctly
      return this.createNoActionResult("King is not stuck");
    }

    this.addLog(
      `🚨 KING BACKUP SUPPORT: King is stuck with ${kingStuckInfo.safeMoves} safe moves`
    );
    this.addLog(
      `🛡️ Prioritizing pieces moving closer to king for protection`
    );

    // Score moves with king support priority
    const scoredMoves = this.scoreMovesForKingSupport(
      gameState,
      allMoves,
      kingStuckInfo.kingPosition
    );

    // Filter to moves that help the king
    const supportMoves = scoredMoves.filter((sm) => sm.score > 0);

    if (supportMoves.length > 0) {
      const bestMoves = supportMoves
        .sort((a, b) => b.score - a.score)
        .slice(0, Math.max(2, Math.floor(supportMoves.length * 0.7)))
        .map((sm) => sm.move);

      this.addLog(
        `✅ Found ${supportMoves.length} king support moves, keeping ${bestMoves.length} best`
      );
      this.addLog(
        `🎯 Average support score: ${(
          supportMoves.reduce((sum, sm) => sum + sm.score, 0) /
          supportMoves.length
        ).toFixed(1)}`
      );

      return this.createFilterResult(
        bestMoves,
        "King backup support - moving pieces closer to protect stuck king",
        {
          logs: this.analysisLogs,
          movesFiltered: allMoves.length - bestMoves.length,
          kingSupportMode: true,
          kingPosition: kingStuckInfo.kingPosition,
          supportMovesFound: supportMoves.length,
        }
      );
    }

    // No supporting moves found
    this.addLog(`❌ No effective king support moves found`);
    return this.createNoActionResult("No effective king support moves available");
  }

  /**
   * Detect if our king is stuck with few or no safe moves
   */
  private detectKingStuckSituation(
    gameState: GameState,
    allMoves: MoveInfo[]
  ): {
    isStuck: boolean;
    safeMoves: number;
    kingPosition: Position | null;
  } {
    const isLightTurn = gameState.isLightTurn;

    // Find our king
    const kingPosition = this.findOurKing(gameState, isLightTurn);
    if (!kingPosition) {
      return { isStuck: false, safeMoves: 0, kingPosition: null };
    }

    // Count king moves in the available moves
    const kingMoves = allMoves.filter(
      (move) =>
        move.fromPosition.row === kingPosition.row &&
        move.fromPosition.col === kingPosition.col
    );

    // King is considered "stuck" if it has very few moves available
    // (indicating that King Safety filtered out most dangerous moves)
    const isStuck = kingMoves.length <= 2;

    return {
      isStuck,
      safeMoves: kingMoves.length,
      kingPosition,
    };
  }

  /**
   * Score moves based on how well they support a stuck king
   */
  private scoreMovesForKingSupport(
    gameState: GameState,
    moves: MoveInfo[],
    kingPosition: Position
  ): Array<{ move: MoveInfo; score: number }> {
    return moves.map((move) => ({
      move,
      score: this.calculateKingSupportScore(gameState, move, kingPosition),
    }));
  }

  /**
   * Calculate how well a move supports the stuck king
   */
  private calculateKingSupportScore(
    gameState: GameState,
    move: MoveInfo,
    kingPosition: Position
  ): number {
    let score = 0;
    const toPosition = move.move.position;
    const fromPosition = move.fromPosition;
    const movingPiece = this.getPieceAt(gameState, fromPosition);

    if (!movingPiece || movingPiece.isKing) return 0; // Don't score king moves

    // 1. PROXIMITY BONUS: Heavily reward moving closer to the king
    const oldDistance = this.calculateDistance(fromPosition, kingPosition);
    const newDistance = this.calculateDistance(toPosition, kingPosition);
    const proximityImprovement = oldDistance - newDistance;

    if (proximityImprovement > 0) {
      score += proximityImprovement * 40; // Major bonus for getting closer
    } else if (proximityImprovement < 0) {
      score += proximityImprovement * 20; // Penalty for moving away
    }

    // 2. ADJACENCY BONUS: Extra points for being adjacent to king
    if (newDistance === 1) {
      score += 30; // Big bonus for being next to the king
    } else if (newDistance === 2) {
      score += 15; // Medium bonus for being close
    }

    // 3. DEFENSIVE POSITION: Bonus for blocking common attack paths
    if (this.isDefensivePosition(toPosition, kingPosition)) {
      score += 25;
    }

    // 4. PIECE-SPECIFIC SUPPORT: Different pieces provide different support
    const supportBonus = this.calculatePieceSpecificSupport(
      movingPiece,
      toPosition,
      kingPosition
    );
    score += supportBonus;

    // 5. MINOR ADVANCEMENT: Still give some credit for forward progress
    const advancementValue = this.calculateAdvancementValue(
      fromPosition,
      toPosition,
      gameState.isLightTurn
    );
    score += advancementValue * 5; // Much lower weight than normal

    return score;
  }

  /**
   * Calculate distance between two positions
   */
  private calculateDistance(pos1: Position, pos2: Position): number {
    return Math.abs(pos1.row - pos2.row) + Math.abs(pos1.col - pos2.col);
  }

  /**
   * Check if a position is defensive relative to the king
   */
  private isDefensivePosition(
    position: Position,
    kingPosition: Position
  ): boolean {
    // Check if the position is between the king and the enemy side
    const isLightKing = kingPosition.row >= 4; // Rough guess based on position

    if (isLightKing) {
      // Light king - defensive positions are in front (lower row numbers)
      return (
        position.row < kingPosition.row &&
        Math.abs(position.col - kingPosition.col) <= 1
      );
    } else {
      // Dark king - defensive positions are in front (higher row numbers)
      return (
        position.row > kingPosition.row &&
        Math.abs(position.col - kingPosition.col) <= 1
      );
    }
  }

  /**
   * Calculate piece-specific support value
   */
  private calculatePieceSpecificSupport(
    piece: IPiece,
    position: Position,
    kingPosition: Position
  ): number {
    switch (piece.type) {
      case PieceType.Bear:
        return 10; // Bears are good defenders
      case PieceType.Rhino:
        return 8; // Rhinos can block paths
      case PieceType.Fungi:
        return 6; // Fungi can support but are weaker
      case PieceType.Wolf:
        return 12; // Wolves are aggressive supporters
      case PieceType.Crane:
        return 15; // Cranes have good range for support
      default:
        return 5; // Default support value
    }
  }

  /**
   * Calculate advancement value (toward enemy territory)
   */
  private calculateAdvancementValue(
    fromPosition: Position,
    toPosition: Position,
    isLight: boolean
  ): number {
    // Light advances toward row 7, Dark advances toward row 0
    const targetRow = isLight ? 7 : 0;

    const oldDistance = Math.abs(fromPosition.row - targetRow);
    const newDistance = Math.abs(toPosition.row - targetRow);

    // Positive score if advancing, negative if retreating
    return oldDistance - newDistance;
  }

  /**
   * Find our king on the board
   */
  private findOurKing(
    gameState: GameState,
    isLightTurn: boolean
  ): Position | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isKing && piece.isLight === isLightTurn) {
          return { row, col };
        }
      }
    }
    return null;
  }

  // Utility methods
  private getPieceAt(gameState: GameState, position: Position): IPiece | null {
    if (!this.isValidPosition(position)) return null;
    const pieces = gameState.board[position.row][position.col].Pieces;
    return pieces.length > 0 ? pieces[0] : null;
  }

  private isValidPosition(position: Position): boolean {
    return (
      position.row >= 0 &&
      position.row < 8 &&
      position.col >= 0 &&
      position.col < 8
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }
} 
